/**
 * KPI Drivers view — horizontal bar chart showing which creative attributes
 * drive KPI performance. Uses useKpiDrivers hook for data.
 *
 * FR-31: Portfolio average reference line
 * FR-32: Bar coloring based on higherIsBetter
 * FR-33/34: KPI selector + scope filter chips
 * FR-35: Type badge per bar (dim/tax/gate/place)
 * FR-36: Top insight callout
 * FR-37: Click handler navigates to Creatives tab filtered by attribute
 */

import { Star, Circle, BarChart3 } from 'lucide-react';
import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { SkeletonCard } from '@/components/dashboard/shared/SkeletonLoaders';
import { useKpiDrivers } from '@/hooks/useDashboard';
import { fmtKpiValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import { META_KPI_CATALOG } from '@/types/dashboard';
import type { KpiDriver } from '@/types/dashboard';

// ============================================================================
// CONSTANTS
// ============================================================================

const SHOW_TOP_N = 5;

type ScopeFilter = 'all' | 'dimension' | 'taxonomy' | 'gate' | 'placement';

const SCOPE_OPTIONS: { value: ScopeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'dimension', label: 'Dimensions' },
  { value: 'taxonomy', label: 'Taxonomy' },
  { value: 'gate', label: 'Gates' },
  { value: 'placement', label: 'Placement' },
];

const TYPE_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  dimension: { bg: 'bg-brand/10', text: 'text-brand' },
  taxonomy: { bg: 'bg-success/10', text: 'text-success' },
  gate: { bg: 'bg-danger/10', text: 'text-danger' },
  placement: { bg: 'bg-warning/10', text: 'text-warning' },
};

const TYPE_SHORT: Record<string, string> = {
  dimension: 'dim',
  taxonomy: 'tax',
  gate: 'gate',
  placement: 'place',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function KpiDrivers() {
  const { filters, setFilter, activeTemplate } = useDashboardContext();

  const primaryKpiId = activeTemplate?.kpiConfig?.primaryKpiId ?? null;
  const secondaryKpiIds = activeTemplate?.kpiConfig?.secondaryKpiIds ?? [];

  // Default KPI: primary or first in catalog
  const defaultKpiId = primaryKpiId ?? META_KPI_CATALOG[0]?.id ?? 'ctr';

  const [selectedKpiId, setSelectedKpiId] = useState(defaultKpiId);
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [showAll, setShowAll] = useState(false);

  // Map frontend scope labels to backend scope values
  const scopeToApi: Record<ScopeFilter, string> = {
    all: 'all',
    dimension: 'dimensions',
    taxonomy: 'taxonomy',
    gate: 'gates',
    placement: 'placement',
  };
  const apiScope = scopeToApi[scope];

  const { data, isLoading, isError } = useKpiDrivers(
    filters,
    selectedKpiId,
    apiScope as 'smart' | 'goal' | 'bucket' | 'all'
  );

  const driverGroup = data;
  // TODO: edgeState not available after api.get envelope unwrap — needs dedicated endpoint or response restructuring
  const isSmallSample = false;

  // Get KPI role for labeling
  function getKpiRole(kpiId: string): 'primary' | 'secondary' | null {
    if (kpiId === primaryKpiId) return 'primary';
    if (secondaryKpiIds.includes(kpiId)) return 'secondary';
    return null;
  }

  if (isLoading) {
    return <SkeletonCard lines={6} />;
  }

  if (isError || !driverGroup) {
    return (
      <EmptyState
        title="No KPI driver data available"
        description="Connect Meta performance data to your creatives to see KPI driver analysis."
      />
    );
  }

  // Apply client-side scope filter on drivers (type uses short codes: dim, tax, gate, place)
  const scopeToType: Record<ScopeFilter, string> = {
    all: 'all',
    dimension: 'dim',
    taxonomy: 'tax',
    gate: 'gate',
    placement: 'place',
  };
  const filteredDrivers = scope === 'all'
    ? driverGroup.drivers
    : driverGroup.drivers.filter((d: KpiDriver) => d.type === scopeToType[scope]);

  const visibleDrivers = showAll ? filteredDrivers : filteredDrivers.slice(0, SHOW_TOP_N);
  const hasMore = filteredDrivers.length > SHOW_TOP_N;

  // Chart data
  const chartData = visibleDrivers.map((d: KpiDriver) => {
    const aboveAvg = d.kpiValue >= driverGroup.portfolioAvg;
    const isGood = driverGroup.higherIsBetter ? aboveAvg : !aboveAvg;
    return {
      name: d.label || `${d.fieldName}: ${d.value}`,
      label: d.label || `${d.fieldName}: ${d.value}`,
      value: d.kpiValue,
      effectSize: d.effectSize,
      count: d.creativeCount,
      isGood,
      type: d.type,
      fieldName: d.fieldName,
      driverValue: d.value,
    };
  });

  // Top insight callout
  const topDriver = filteredDrivers[0];
  const worstDriver = filteredDrivers[filteredDrivers.length - 1];

  // CSV data
  const csvHeaders = ['Attribute', 'Type', 'KPI Value', 'Effect Size', 'Creative Count'];
  const csvRows = filteredDrivers.map((d: KpiDriver) => [
    d.label || `${d.fieldName}: ${d.value}`,
    d.type,
    d.kpiValue,
    d.effectSize,
    d.creativeCount,
  ]);

  // Click handler: drilldown to Creatives tab
  function handleBarClick(entry: typeof chartData[0]) {
    setFilter('activeTab', 'creatives');
    setFilter('drilldownAttribute', `${entry.fieldName}:${entry.driverValue}`);
  }

  return (
    <div className="space-y-4">
      {isSmallSample && (
        <div className="rounded-[10px] bg-warning/10 border border-[#F59E0B]/30 px-4 py-2">
          <span className="text-xs text-secondary">
            Small sample -- driver rankings may be unreliable.
          </span>
        </div>
      )}

      <div className="rounded-[14px] bg-surface shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
          <div>
            <div className="text-lg font-semibold text-primary">
              {driverGroup.kpiLabel} Drivers
            </div>
            <div className="text-xs text-muted mt-0.5">
              Portfolio avg: {fmtKpiValue(driverGroup.portfolioAvg, driverGroup.kpiUnit)}
              {' '} -- {driverGroup.higherIsBetter ? 'Higher = better (green)' : 'Lower = better (green)'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* KPI selector */}
            <select
              value={selectedKpiId}
              onChange={(e) => {
                setSelectedKpiId(e.target.value);
                setShowAll(false);
              }}
              className="text-xs border border-border-subtle rounded px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand max-w-[200px]"
            >
              {META_KPI_CATALOG.map((kpi) => {
                const role = getKpiRole(kpi.id);
                const suffix = role === 'primary' ? ' * Primary' : role === 'secondary' ? ' . Secondary' : '';
                return (
                  <option key={kpi.id} value={kpi.id}>
                    {kpi.label}{suffix}
                  </option>
                );
              })}
              <option value="gatePassRate">Gate Pass Rate</option>
            </select>
            {/* Legend */}
            <div className="flex items-center gap-2 text-[10px] text-muted">
              {primaryKpiId && (
                <span className="flex items-center gap-0.5">
                  <Star size={10} className="text-brand fill-brand" />Primary
                </span>
              )}
              {secondaryKpiIds.length > 0 && (
                <span className="flex items-center gap-0.5">
                  <Circle size={10} className="text-muted" />Secondary
                </span>
              )}
            </div>
            <CsvExport
              headers={csvHeaders}
              rows={csvRows}
              filename={`kpi-drivers-${selectedKpiId}`}
            />
          </div>
        </div>

        {/* Scope filter chips */}
        <div className="flex items-center gap-1.5 px-4 py-2 border-b border-border-subtle bg-canvas">
          <span className="text-[10px] text-muted font-medium mr-1">Scope:</span>
          {SCOPE_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setScope(value)}
              className={cn(
                'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                scope === value
                  ? 'bg-brand text-white'
                  : 'bg-surface border border-border-subtle text-secondary hover:bg-surface-subtle'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="px-4 py-3">
          {filteredDrivers.length === 0 ? (
            <div className="py-6">
              {driverGroup.drivers.length > 0 ? (
                <div className="text-center text-xs text-muted">
                  No {SCOPE_OPTIONS.find(s => s.value === scope)?.label.toLowerCase()} drivers found for {driverGroup.kpiLabel}. Try a different scope filter.
                </div>
              ) : (
                <div className="text-center">
                  <BarChart3 size={24} className="mx-auto text-muted mb-2" />
                  <div className="text-sm font-semibold text-secondary">
                    {driverGroup.creativesWithData != null && driverGroup.creativesWithData === 0
                      ? 'No data available'
                      : driverGroup.creativesWithData != null && driverGroup.totalCreatives != null && driverGroup.creativesWithData === driverGroup.totalCreatives
                        ? 'No shared attribute groups'
                        : 'Not enough data yet'}
                  </div>
                  <div className="text-xs text-muted mt-1 max-w-md mx-auto">
                    {driverGroup.creativesWithData != null && driverGroup.totalCreatives != null ? (
                      driverGroup.creativesWithData === 0 ? (
                        <>
                          None of your {driverGroup.totalCreatives} creative{driverGroup.totalCreatives !== 1 ? 's' : ''} have{' '}
                          {driverGroup.kpiLabel} data yet. Connect Meta performance data to unlock driver analysis.
                        </>
                      ) : driverGroup.creativesWithData === driverGroup.totalCreatives ? (
                        <>
                          All {driverGroup.totalCreatives} creatives have {driverGroup.kpiLabel} data, but no attribute group
                          has 2+ creatives with the same value. Try a broader filter or add more creatives with shared attributes.
                        </>
                      ) : (
                        <>
                          {driverGroup.creativesWithData} of {driverGroup.totalCreatives} creative{driverGroup.totalCreatives !== 1 ? 's' : ''} have{' '}
                          {driverGroup.kpiLabel} data. Driver analysis needs at least 2 creatives per attribute group
                          with the same KPI data.
                        </>
                      )
                    ) : (
                      <>
                        Driver analysis requires multiple creatives with {driverGroup.kpiLabel} data
                        grouped by the same attribute to compute statistical effect sizes.
                      </>
                    )}
                  </div>
                  {driverGroup.creativesWithData != null && driverGroup.creativesWithData > 0 && driverGroup.totalCreatives != null && (
                    <div className="mt-3 mx-auto max-w-xs">
                      <div className="flex items-center justify-between text-[10px] text-muted mb-1">
                        <span>Data coverage</span>
                        <span>{driverGroup.creativesWithData}/{driverGroup.totalCreatives}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-surface-subtle overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand transition-all"
                          style={{ width: `${Math.round((driverGroup.creativesWithData / driverGroup.totalCreatives) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Top insight callout (FR-36) */}
              {topDriver && (
                <div className="mb-3 rounded-[10px] bg-surface-subtle px-3 py-2">
                  <span className="text-xs text-secondary">
                    Best: <strong>{topDriver.label || topDriver.value}</strong> ({fmtKpiValue(topDriver.effectSize ?? 0, 'x')} avg)
                    {worstDriver && worstDriver !== topDriver && (
                      <> | Worst: <strong>{worstDriver.label || worstDriver.value}</strong> ({fmtKpiValue(worstDriver.effectSize ?? 0, 'x')} avg)</>
                    )}
                  </span>
                </div>
              )}

              <div className="relative">
                <ResponsiveContainer width="100%" height={chartData.length * 36 + 20}>
                  <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 4, right: 80, bottom: 4, left: 120 }}
                  >
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: '#8B95A7' }}
                      tickFormatter={(v: number) => `${v}${driverGroup.kpiUnit}`}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={110}
                      tick={{ fontSize: 11, fill: '#5C677A' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value: number, _name: string, entry: any) => [
                        `${fmtKpiValue(value ?? 0, driverGroup.kpiUnit)} (${fmtKpiValue(entry.payload.effectSize ?? 0, 'x')} avg, n=${entry.payload.count})`,
                        entry.payload.label,
                      ]}
                      contentStyle={{
                        fontSize: 11,
                        borderRadius: 8,
                        border: '1px solid rgb(var(--color-border-subtle))',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                    />
                    <ReferenceLine
                      x={driverGroup.portfolioAvg}
                      stroke="#8B95A7"
                      strokeDasharray="4 4"
                      label={{ value: 'avg', position: 'insideTopRight', fontSize: 10, fill: '#8B95A7' }}
                    />
                    <Bar
                      dataKey="value"
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                      onClick={(entry: typeof chartData[0]) => handleBarClick(entry)}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.isGood ? '#22C55E' : '#EF4444'}
                          fillOpacity={0.85}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                {/* Type badges overlay (FR-35) */}
                <div
                  className="absolute top-0 right-0 flex flex-col justify-around"
                  style={{ height: chartData.length * 36 + 20, paddingTop: 12, paddingBottom: 12 }}
                >
                  {chartData.map((entry, i) => {
                    const badge = TYPE_BADGE_COLORS[entry.type] ?? { bg: 'bg-gray-100', text: 'text-gray-600' };
                    return (
                      <div
                        key={i}
                        className={cn('text-[9px] px-1 py-0.5 rounded font-medium', badge.bg, badge.text)}
                        style={{ lineHeight: '1' }}
                      >
                        {TYPE_SHORT[entry.type] ?? entry.type}
                      </div>
                    );
                  })}
                </div>
              </div>

              {hasMore && (
                <div className="mt-1 text-right">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="text-xs text-brand hover:underline"
                  >
                    {showAll ? 'Show top 5 only' : `Show all ${filteredDrivers.length} drivers`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
